package dev.kuml.portal.web.editor

import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.dom.ARRAYBUFFER
import org.w3c.dom.BinaryType
import org.w3c.dom.MessageEvent
import org.w3c.dom.WebSocket
import org.w3c.dom.events.Event

/**
 * Impure WebSocket glue for the collaboration connection -- NOT covered by `jsNodeTest` (no real
 * DOM/WebSocket in that runtime), matching this module's established convention for browser-only
 * code (see `README.adoc`'s "Why jsNodeTest, not jsBrowserTest"): verified manually against the
 * live deployment instead. Every DECISION this class drives lives in the pure, DOM-free files next
 * to it -- [CollabFrame] (wire parsing), [CollabRoster] (presence bookkeeping),
 * [CollabPresenceFormat] (indicator copy), [CollabReconnect] (reconnect state machine),
 * [isValidDiagramId] (project-id format validation before it is woven into the WebSocket URL, see
 * `CollabDiagramId.kt`'s KDoc) -- so this class itself stays as thin as possible: read the
 * DOM/WebSocket event, hand it to the pure logic, act on the pure result.
 *
 * One instance per open editor screen (see `EditorScreen.kt`). Same-origin exactly like
 * [dev.kuml.portal.web.api.PortalApi]'s fetch calls -- `ws://`/`wss://` matching the page's own
 * scheme, same host, `/api/diagrams/{diagramId}/collab`. The existing httpOnly session cookie is sent
 * automatically by the browser on the WebSocket upgrade request for a same-origin URL, exactly
 * like any other same-origin request to that host -- no token handling needed here.
 *
 * R-28: cursor/selection sharing rides this same presence channel now -- the presence frame sent on
 * open carries [PresencePayload.INITIAL] (a valid, empty v1 payload) rather than the pre-R-28
 * trivial `"$tabId:$joinedAtMillis"` liveness string; [sendPresence] sends every subsequent update
 * (throttled by `EditorScreen.kt`'s [PresenceScheduler]); and an incoming presence frame's opaque
 * payload is handed to [onPeerPresenceChanged] UNPARSED (see that parameter's own KDoc for why the
 * actual [PresencePayload] JSON decoding happens in `EditorScreen.kt`, not here). No client-side
 * Loro/CRDT parsing either: a [CollabFrame.KIND_CRDT] frame is never decoded, only treated as a
 * signal to re-fetch the rendered diagram via [onRefreshNeeded].
 *
 * @param onRefreshNeeded invoked whenever a [CollabFrame.KIND_CRDT] frame arrives -- the project's
 *   diagram changed server-side (another collaborator's edit, or an AI regeneration) and the
 *   caller should re-fetch and re-render it. **R-27:** also invoked for a server-to-client
 *   `FRAME_KIND_MODEL_REV` frame (decoded via [ModelOpCodec.decodeRevision]) -- unlike a raw CRDT
 *   import, [CollabRoomRegistry.applyOpAndBroadcast] never also sends a [CollabFrame.KIND_CRDT]
 *   frame alongside it (see `CollabWireProtocol.kt`'s class KDoc "V4"), so `MODEL_REV` is itself
 *   this connection's only "the live model changed, re-fetch render + outline" signal for an
 *   inspector-panel edit -- this connection's own, and every other collaborator's.
 * @param onPeersChanged invoked with the current list of *other* connected-and-identified peers
 *   (R-16) every time the roster or an identity changes -- `list.size` is the same count
 *   [CollabPresenceFormat.indicatorText] already formats, so no separate count callback remains.
 * @param onModelOpRejected R-27: invoked for a `FRAME_KIND_MODEL_OP_REJECT` frame (decoded via
 *   [ModelOpCodec.decodeReject]) -- sent back to THIS connection alone when [sendOp]'s op fails
 *   server-side validation. Per the design-team ruling ("abgelehnte Op = Statuszeile, Verbindung
 *   bleibt offen") a rejection never closes the connection -- the caller is expected to surface
 *   [ModelOpFeedback.messageFor] in the status line and nothing else.
 * @param onPeerPresenceChanged R-28: invoked for every [CollabFrame.KIND_PRESENCE] frame this
 *   connection receives -- BEFORE it is folded into [roster]/[identities] and re-surfaces via
 *   [onPeersChanged], so a caller sees both callbacks for the very same frame if it cares about
 *   both. [rawPayload] is [CollabPresenceEvent.payload] verbatim, still UNDECODED JSON (`""` when
 *   `present` is `false`) -- this class's own KDoc explains why: `CollabClient` stays thin, reading
 *   the wire and handing raw results to pure logic elsewhere, and [PresencePayload.decode] needs
 *   `EditorScreen.kt`'s own `latestOutline`/current viewBox to do anything meaningful with it, both
 *   of which this class deliberately knows nothing about. **Post-review fix:** also invoked
 *   synthetically -- `present = false`, `rawPayload = ""`, exactly like a real disconnect frame --
 *   for every still-known roster entry when [CollabReconnect] gives up for good (see
 *   [handleUnintendedClose]'s `GaveUp` branch), so a caller's presence bookkeeping (in particular
 *   any per-peer SELECTION state with no TTL of its own) is torn down the same way a genuine
 *   per-peer `present = false` frame would tear it down, instead of only [onPeersChanged] firing
 *   with an empty list.
 */
public class CollabClient(
    private val diagramId: String,
    private val onRefreshNeeded: () -> Unit,
    private val onPeersChanged: (List<CollabPeer>) -> Unit,
    private val onModelOpRejected: (ModelOpReject) -> Unit = {},
    private val onPeerPresenceChanged: (tokenHex: String, present: Boolean, rawPayload: String) -> Unit = { _, _, _ -> },
) {
    private var socket: WebSocket? = null
    private var phase: CollabPhase = CollabPhase.Connecting
    private var closingIntentionally = false
    private var roster: Set<String> = emptySet()
    private var identities: Map<String, CollabPeer> = emptyMap()

    public fun connect() {
        closingIntentionally = false
        phase = CollabPhase.Connecting
        openSocket()
    }

    /** Sets [closingIntentionally] first, so [handleUnintendedClose] never fires for this close --
     * used both for the give-up case internally and for `EditorScreenHandle.dispose()` externally. */
    public fun close() {
        closingIntentionally = true
        socket?.close()
        socket = null
    }

    /**
     * R-27: sends one committed inspector-panel edit as a `FRAME_KIND_MODEL_OP` frame (see
     * [ModelOpCodec.encode]'s KDoc "sent only on commit, never per keystroke" discipline). A quiet
     * no-op -- never throws, never queues -- when [phase] is not [CollabPhase.Live]: there is no
     * live room to apply it against (mirrors the server's own [dev.kuml.portal.server.collab
     * .ModelOpRejectReason.NO_LIVE_ROOM] outcome for the same situation, just without a round trip
     * to discover it). The caller (`InspectorPanel.kt` via `EditorScreen.kt`) has no separate
     * "am I connected" check of its own -- an op simply goes nowhere while disconnected, and the
     * next successful reconnect's fresh [CollabFrame.KIND_CRDT] snapshot already re-syncs the
     * diagram from the server's authoritative state regardless.
     */
    public fun sendOp(op: ClientModelOp) {
        val ws = socket ?: return
        if (phase != CollabPhase.Live) return
        ws.send(ModelOpCodec.encode(op).toArrayBuffer())
    }

    /**
     * R-28: sends one [PresencePayload]-encoded snapshot as a [CollabFrame.KIND_PRESENCE] frame --
     * [payload] is the encoded bytes WITHOUT the kind byte (see [PresencePayload.encode]'s own
     * KDoc), which this method prepends. Same quiet no-op discipline as [sendOp] when [phase] is
     * not [CollabPhase.Live] -- there is no live room to publish presence into, and the next
     * successful reconnect's [PresencePayload.INITIAL] open-frame re-establishes this connection's
     * presence from scratch regardless (`EditorScreen.kt` does not queue anything while
     * disconnected).
     */
    public fun sendPresence(payload: ByteArray) {
        val ws = socket ?: return
        if (phase != CollabPhase.Live) return
        ws.send((byteArrayOf(CollabFrame.KIND_PRESENCE) + payload).toArrayBuffer())
    }

    private fun openSocket() {
        val url = collabUrl(diagramId)
        if (url == null) {
            // Fails closed rather than ever constructing a WebSocket from an unvalidated
            // diagramId (minor security-review finding, 2026-07-27 -- see CollabDiagramId.kt's
            // KDoc). Quiet, exactly like handleUnintendedClose's GaveUp branch: no error toast,
            // the presence indicator simply never appears. Not expected to ever trigger in
            // practice -- diagramId always comes from a server-issued UUID during normal
            // navigation -- this is a defense-in-depth guard, not a real user-facing path.
            phase = CollabPhase.GaveUp
            roster = emptySet()
            identities = emptyMap()
            onPeersChanged(emptyList())
            return
        }
        val ws = WebSocket(url)
        ws.binaryType = BinaryType.ARRAYBUFFER
        // Browsers commonly fire `onerror` followed by `onclose` for the very same failed/dropped
        // connection (and some abnormal closures fire only one or the other) -- without this guard,
        // BOTH would call handleUnintendedClose(), double-advancing CollabReconnect's state machine
        // for what is really only ONE disconnect. Since CollabReconnect.MAX_ATTEMPTS is 1 ("exactly
        // one retry then give up"), a double-advance either jumps straight past the intended single
        // retry to GaveUp (while a stale window.setTimeout from the superseded first transition
        // still reopens a socket later), or -- if the disconnect happens while already
        // Reconnecting -- bounces GaveUp back into a fresh Reconnecting(1) instead of staying given
        // up. Scoped as a local var (not a class-level field) so it is naturally fresh for every
        // [openSocket] call/socket instance, with nothing to reset by hand on reconnect.
        var unintendedCloseHandled = false

        fun handleUnintendedCloseOnce() {
            if (unintendedCloseHandled) return
            unintendedCloseHandled = true
            handleUnintendedClose()
        }
        ws.onopen = {
            phase = CollabPhase.Live
            // R-28: PresencePayload.INITIAL (a valid, empty v1 payload) replaces the pre-R-28
            // trivial "$tabId:$joinedAtMillis" liveness string -- still non-empty, so this
            // connection is still immediately `present = true` for every peer, just in the new v1
            // wire format from the very first frame onward.
            ws.send((byteArrayOf(CollabFrame.KIND_PRESENCE) + PresencePayload.INITIAL).toArrayBuffer())
            Unit
        }
        ws.onmessage = { event: Event ->
            val data = (event as MessageEvent).data
            val buffer = data as? ArrayBuffer
            if (buffer != null) handleFrame(buffer.toByteArrayFromBuffer())
            Unit
        }
        ws.onclose = { handleUnintendedCloseOnce() }
        ws.onerror = { handleUnintendedCloseOnce() }
        socket = ws
    }

    private fun handleFrame(bytes: ByteArray) {
        if (CollabFrame.isRefreshSignal(bytes)) {
            onRefreshNeeded()
            return
        }
        // R-27: FRAME_KIND_MODEL_REV (kind 4) / FRAME_KIND_MODEL_OP_REJECT (kind 5) -- checked
        // BEFORE decodeIdentity/decodePresence below, which only ever recognise kind 2/1 and would
        // otherwise silently drop both (see ModelOpCodec.kt's class KDoc for the wire shapes).
        ModelOpCodec.decodeRevision(bytes)?.let {
            onRefreshNeeded()
            return
        }
        ModelOpCodec.decodeReject(bytes)?.let { reject ->
            onModelOpRejected(reject)
            return
        }
        CollabFrame.decodeIdentity(bytes)?.let { identity ->
            identities = CollabRoster.withIdentity(identities, identity)
            onPeersChanged(CollabRoster.peersOf(roster, identities))
            return
        }
        val presence = CollabFrame.decodePresence(bytes) ?: return
        if (presence.present) {
            roster = CollabRoster.withPresent(roster, presence.tokenHex)
        } else {
            roster = CollabRoster.withAbsent(roster, presence.tokenHex)
            identities = CollabRoster.withoutIdentity(identities, presence.tokenHex)
        }
        // R-28: fired BEFORE onPeersChanged below (see this class's own KDoc on the parameter) --
        // order between the two is otherwise inconsequential, both are plain callbacks.
        onPeerPresenceChanged(presence.tokenHex, presence.present, presence.payload)
        onPeersChanged(CollabRoster.peersOf(roster, identities))
    }

    private fun handleUnintendedClose() {
        if (closingIntentionally) return
        phase = CollabReconnect.afterUnintendedClose(phase)
        when (phase) {
            is CollabPhase.Reconnecting ->
                window.setTimeout({ if (!closingIntentionally) openSocket() }, CollabReconnect.RETRY_DELAY_MS)
            CollabPhase.GaveUp -> {
                // Quiet -- no error toast (see CollabReconnect's KDoc): the presence indicator
                // simply disappears since nobody else is reachable anymore from this tab's view.
                //
                // R-28 fix (post-review, see this class's own KDoc on `onPeerPresenceChanged`):
                // a real per-peer disconnect always reaches the caller as a `present = false`
                // presence frame via `onPeerPresenceChanged`, which is the ONLY path that clears
                // `EditorScreen.kt`'s `presenceState` (cursor/selection). Giving up on reconnection
                // is not a per-peer event -- it is this connection unilaterally deciding every
                // roster entry is no longer reachable -- but from the caller's presence-state point
                // of view it must look identical, otherwise a peer's last-known SELECTION (which,
                // unlike its cursor, has no TTL by design -- see `PresenceState.CURSOR_TTL_MS`'s
                // KDoc) stays stuck forever behind an avatar strip that has already gone to zero.
                // So every currently-known roster entry gets its synthetic "gone" frame BEFORE the
                // roster/identities/onPeersChanged cleanup below, exactly mirroring what would have
                // happened had each peer's own `present = false` frame actually arrived. The actual
                // token set is computed by the pure, unit-tested [CollabRoster.tokensToReleaseOnGiveUp]
                // (see its own KDoc) -- this class stays thin, just acting on that result.
                CollabRoster.tokensToReleaseOnGiveUp(roster).forEach { tokenHex ->
                    onPeerPresenceChanged(tokenHex, false, "")
                }
                roster = emptySet()
                identities = emptyMap()
                onPeersChanged(emptyList())
            }
            else -> Unit
        }
    }
}

/**
 * `null` if [diagramId] is not a well-formed UUID (see [isValidDiagramId]'s KDoc, minor
 * security-review finding, 2026-07-27) -- [openSocket] treats that as a fail-closed no-op rather
 * than ever handing an unvalidated value to the `WebSocket` constructor.
 */
private fun collabUrl(diagramId: String): String? {
    if (!isValidDiagramId(diagramId)) return null
    val scheme = if (window.location.protocol == "https:") "wss" else "ws"
    return "$scheme://${window.location.host}/api/diagrams/$diagramId/collab"
}

private fun ByteArray.toArrayBuffer(): ArrayBuffer {
    val view = Int8Array(size)
    for (i in indices) view[i] = this[i]
    return view.buffer
}

private fun ArrayBuffer.toByteArrayFromBuffer(): ByteArray {
    val view = Int8Array(this)
    return ByteArray(view.length) { view[it] }
}
